import { defaultsDeep } from 'lodash-es';
import { getMoveIQAttributes } from './common/analytics';

const createEvent = type => (verb, event, properties) =>
  new CustomEvent('upd-moveiq-analytics', {
    bubbles: true,
    composed: true,
    detail: {
      type,
      event: [verb, 'MoveIQ', event].filter(Boolean).join(' '),
      properties: {
        ...properties,
        ...getMoveIQAttributes(),
      },
    },
  });

export const page = createEvent('page');
export const track = createEvent('track');

/**
 * @param dataset An element's `dataset` property.
 * https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/dataset
 * @return {Object} A nested object representation of `dataset` (DOMStringMap).
 *
 * @example
 ```html
 <div
   id="myElement"
   data-analytics-event="My Event"
   data-analytics-properties-value="My Event Value"
 >
 ```
 ```js
 const { dataset } = document.querySelector('#myElement');
 const datasetObj = datasetToObject(dataset);

 console.log(dataset.analyticsEvent, dataset.analyticsPropertiesValue);
 // --> "My Event", "My Event Value"

 console.log(datasetObj.analytics.event, datasetObj.analytics.properties.value);
 // --> "My Event", "My Event Value"
 ```
 */
const datasetToObject = dataset =>
  defaultsDeep(
    {},
    ...Object.keys(dataset).map(key =>
      key
        .replace(/([A-Z])/g, (_, match) => ` ${match.toLowerCase()}`)
        .split(' ')
        .reduceRight((obj, prop) => ({ [prop]: obj }), dataset[key])
    )
  );

/**
 * Flattens a nested object's keys, camelCasing the resulting keys.
 * E.g. `{ foo: { bar: { baz: 'foo' } } } => { fooBarBaz: 'foo' }`
 *
 * @param {Object} object
 * @param {String} [prefix] Used in recursion.
 * @return {Object}
 */
const camelFlattenKeys = (object, prefix = '') => {
  const toCamel = str => str.slice(0, 1).toUpperCase() + str.slice(1);

  return Object.keys(object).reduce((result, key) => {
    const camelKey = prefix.length ? toCamel(key) : key;

    return {
      ...result,
      ...(typeof object[key] === 'object'
        ? camelFlattenKeys(object[key], `${prefix}${camelKey}`)
        : { [`${prefix}${camelKey}`]: object[key] }),
    };
  }, {});
};

/**
 * Parses the event's "path" (the elements it bubbled through) for the
 * first element that contains a `data` attribute with analytics metadata.
 *
 * @param {Event} event
 * @return {Object} The analytics data that was found (or empty object).
 */
const parseAnalyticsDataFromEvent = event => {
  const path = event.composedPath();
  const element = path.find(el => el.dataset && el.dataset.analyticsEvent);
  return element ? datasetToObject(element.dataset).analytics : {};
};

const csvToArray = csv => csv.split(',').map(str => str.trim());

const TRACKED_EVENTS = {
  change: {
    verb: 'Changed',
    detail: true,
  },
  click: {
    verb: 'Clicked',
    detail: false,
  },
  close: {
    verb: 'Closed',
    detail: false,
  },
  submit: {
    verb: 'Submitted',
    detail: true,
  },
};

/**
 * @param {HTMLElement} element Element to add analytics event listeners to.
 * @return {Function} Function that removes the added listeners.
 */
export const trackOn = element => {
  const eventListeners = Object.keys(TRACKED_EVENTS).map(type => ({
    type,
    listener: domEvent => {
      const { event, properties, on } = parseAnalyticsDataFromEvent(domEvent);
      const detail = TRACKED_EVENTS[type].detail && domEvent.detail;

      if (event && (!on || csvToArray(on).indexOf(type) !== -1)) {
        element.dispatchEvent(
          track(TRACKED_EVENTS[type].verb, event, {
            ...camelFlattenKeys(properties || {}),
            ...(detail ? { value: detail } : {}),
            fromTrackOn: true,
          })
        );
      }
    },
  }));

  eventListeners.forEach(({ type, listener }) =>
    element.addEventListener(type, listener)
  );

  return () =>
    eventListeners.forEach(({ type, listener }) =>
      element.removeEventListener(type, listener)
    );
};

// TODO: Workaround for Analytics. We need to allow for DOM events
// to reach the analytics event handlers before navigating.
// https://codepen.io/stiggler/pen/PRggZq
export const withTimeout = fn => {
  return (...args) =>
    new Promise(resolve => setTimeout(() => resolve(fn.apply(fn, args))));
};
